import {
  CompleteKundali,
  PlanetName,
  ZodiacSign,
  Language,
  DashaPeriod,
  CharaKarakaItem
} from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  PredictiveEventType,
  PredictiveEventWindow,
  TriangulationResult,
  TriangulationEvidenceGroup,
  ParashariDashaEvidence,
  ParashariTransitEvidence,
  KPEvidence,
  JaiminiEvidence,
  SAVModulationEvidence,
  TriangulationTraceItem,
  TriangulationMethod,
  FinalAssessmentStatus,
  EvidenceStatus,
  V519Score,
  V519ConfidenceScore,
  V519DateString
} from '../../types/v519';
import { ZODIAC_SIGNS, ZODIAC_LORDS, getSavByBhava } from '../vedicEngine';
import { calculateKPProfile, KPAnalysisResult } from '../kpEngine';
import { analyzeFiveRuleHouse } from '../fiveRuleHouseEngine';
import { generateTemporalSynthesis } from '../v518/temporalSynthesisEngine';
import {
  EVENT_RULE_REGISTRY,
  EventRuleDefinition,
  getEventRuleDefinition,
  predictLifeEvent
} from './eventPredictionEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.19 — THREE-STREAM METHODOLOGICAL TRIANGULATION ENGINE
   --------------------------------------------------------------------------
   Step 3 Core Engine:
   Determines deterministic cross-methodological corroboration across:
   1. PARASHARI STREAM (Vimshottari Dasha, 5-Rule PAE, SAV, Transits)
   2. KRISHNAMURTI PADDHATI (KP) STREAM (Sub-Lord & 4-Step Significators)
   3. JAIMINI STREAM (Chara Dasha, Chara Karakas, Arudha Lagna, Rashi Drishti)

   ARCHITECTURAL CONTRACT:
   - Zero modifications to frozen V5.17 and V5.18 modules.
   - Pure mathematical, astrological & logical evaluation — zero AI/LLM calls.
   - Corroboration Principle: Triangulation evaluates genuine cross-stream
     agreement and penalizes methodological conflict rather than simple averaging.
   ========================================================================== */

/** Centralized, configurable methodological weights */
export const DEFAULT_TRIANGULATION_WEIGHTS = {
  PARASHARI: 0.40,
  KP: 0.35,
  JAIMINI: 0.25
} as const;

export interface TriangulationWeights {
  readonly PARASHARI: number;
  readonly KP: number;
  readonly JAIMINI: number;
}

export type TemporalAlignmentType =
  | 'EXACT_OVERLAP'
  | 'PARTIAL_OVERLAP'
  | 'MACRO_OVERLAP'
  | 'NO_OVERLAP'
  | 'UNAVAILABLE';

export interface TriangulatedEventWindow extends PredictiveEventWindow {
  readonly triangulationResult: TriangulationResult;
  readonly temporalAgreement: TemporalAlignmentType;
}

/* --------------------------------------------------------------------------
   HELPER UTILITIES: ZODIAC & PLANETARY RULERSHIPS
   -------------------------------------------------------------------------- */

const ALL_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

function getSignIndex(sign: ZodiacSign): number {
  const idx = (ALL_SIGNS || []).indexOf(sign);
  return idx >= 0 ? idx : 0;
}

function getHouseFromSign(lagnaSign: ZodiacSign, targetSign: ZodiacSign): number {
  const lagnaIdx = getSignIndex(lagnaSign);
  const targetIdx = getSignIndex(targetSign);
  return ((targetIdx - lagnaIdx + 12) % 12) + 1;
}

function getPlanetOwnedHouses(planet: PlanetName, lagnaSign: ZodiacSign): number[] {
  const lagnaIdx = getSignIndex(lagnaSign);
  const owned: number[] = [];
  for (let h = 1; h <= 12; h++) {
    const signAtHouse = ALL_SIGNS[(lagnaIdx + (h - 1)) % 12];
    if (ZODIAC_LORDS[signAtHouse] === planet) {
      owned.push(h);
    }
  }
  return owned;
}

/* ==========================================================================
   1. PARASHARI STREAM ADAPTER
   ========================================================================== */

export interface ParashariStreamOutput {
  readonly dashaEvidence: ParashariDashaEvidence;
  readonly transitEvidence: ParashariTransitEvidence;
  readonly savEvidence: SAVModulationEvidence;
  readonly score: V519Score;
  readonly status: EvidenceStatus;
  readonly supportingFactorsEn: string[];
  readonly supportingFactorsHi: string[];
  readonly limitingFactorsEn: string[];
  readonly limitingFactorsHi: string[];
  readonly traces: TriangulationTraceItem[];
}

export function evaluateParashariStream(
  kundali: CompleteKundali,
  eventType: PredictiveEventType,
  monthOffset: number = 0
): ParashariStreamOutput {
  const rule = getEventRuleDefinition(eventType);
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const supportingFactorsEn: string[] = [];
  const supportingFactorsHi: string[] = [];
  const limitingFactorsEn: string[] = [];
  const limitingFactorsHi: string[] = [];
  const traces: TriangulationTraceItem[] = [];

  // 1. Dasha evaluation
  const dashaList: DashaPeriod[] = kundali.dashas || [];
  const currentMD = dashaList.find(d => d.isCurrent) || dashaList[0];
  const currentAD = currentMD?.antardashas?.find(ad => ad.isCurrent) || currentMD?.antardashas?.[0];
  const mdLord: PlanetName = currentMD?.planet || 'Jupiter';
  const adLord: PlanetName = currentAD?.planet || 'Saturn';

  const mdHouses = getPlanetOwnedHouses(mdLord, lagnaSign);
  const adHouses = getPlanetOwnedHouses(adLord, lagnaSign);
  const activeHouses = Array.from(new Set([...mdHouses, ...adHouses]));

  const mdPl = kundali.planets?.find(p => p.name === mdLord);
  const adPl = kundali.planets?.find(p => p.name === adLord);
  if (mdPl) activeHouses.push(mdPl.house);
  if (adPl) activeHouses.push(adPl.house);

  let rawDashaScore = 50;
  const primaryMatches = activeHouses.filter(h => rule.primaryHouses.includes(h));
  const supportingMatches = activeHouses.filter(h => rule.supportingHouses.includes(h));
  const cautionMatches = activeHouses.filter(h => rule.cautionHouses.includes(h));
  const isKarakaActive = rule.naturalKarakas.includes(mdLord) || rule.naturalKarakas.includes(adLord);

  if (primaryMatches.length > 0) {
    rawDashaScore += primaryMatches.length * 12;
    supportingFactorsEn.push(`Parashari Dasha (${mdLord}-${adLord}) activates primary house(s) ${primaryMatches.join(', ')}.`);
    supportingFactorsHi.push(`पराशरी दशा (${mdLord}-${adLord}) मुख्य भाव ${primaryMatches.join(', ')} को सक्रिय कर रही है।`);
    traces.push({
      stream: 'PARASHARI',
      indicator: 'Dasha House Activation',
      finding: `Active Dasha lords ${mdLord}-${adLord} govern primary house(s) ${primaryMatches.join(', ')}`,
      findingHi: `सक्रिय दशा स्वामी ${mdLord}-${adLord} मुख्य भाव ${primaryMatches.join(', ')} के अधिपति हैं`,
      impact: 'POSITIVE',
      weight: 0.30
    });
  }

  if (supportingMatches.length > 0) {
    rawDashaScore += supportingMatches.length * 6;
    supportingFactorsEn.push(`Supporting house(s) ${supportingMatches.join(', ')} energized under current Dasha cycle.`);
    supportingFactorsHi.push(`वर्तमान दशा में सहायक भाव ${supportingMatches.join(', ')} को बल प्राप्त हो रहा है।`);
  }

  if (isKarakaActive) {
    rawDashaScore += 10;
    supportingFactorsEn.push(`Natural Karaka (${rule.naturalKarakas.join('/')}) is activated as current Dasha lord.`);
    supportingFactorsHi.push(`प्राकृतिक कारक (${rule.naturalKarakas.join('/')}) वर्तमान दशा स्वामी के रूप में सक्रिय है।`);
    traces.push({
      stream: 'PARASHARI',
      indicator: 'Natural Karaka Alignment',
      finding: `Dasha lord is natural significator for ${rule.titleEn}`,
      findingHi: `दशा स्वामी ${rule.titleHi} का नैसर्गिक कारक है`,
      impact: 'POSITIVE',
      weight: 0.20
    });
  }

  if (cautionMatches.length > 0) {
    rawDashaScore -= cautionMatches.length * 8;
    limitingFactorsEn.push(`Dasha lord simultaneously activates caution house(s) ${cautionMatches.join(', ')}.`);
    limitingFactorsHi.push(`दशा स्वामी प्रतिकूल/सावधानी भाव ${cautionMatches.join(', ')} को भी सक्रिय कर रहा है।`);
    traces.push({
      stream: 'PARASHARI',
      indicator: 'Dasha Caution House Link',
      finding: `Dasha lords rule caution house(s) ${cautionMatches.join(', ')}`,
      findingHi: `दशा स्वामी सावधानी भाव ${cautionMatches.join(', ')} का स्वामी है`,
      impact: 'NEGATIVE',
      weight: 0.15
    });
  }

  // Five-Rule PAE integration from V5.17
  try {
    const fiveRule = analyzeFiveRuleHouse(kundali, rule.themeId, 'en');
    if (fiveRule && typeof fiveRule.netPromiseScore === 'number') {
      const fiveRuleAdj = Math.round((fiveRule.netPromiseScore - 50) * 0.2);
      rawDashaScore += fiveRuleAdj;
      if (fiveRule.netPromiseScore >= 65) {
        supportingFactorsEn.push(`5-Rule PAE confirms strong natal potential (${fiveRule.netPromiseScore}/100) for ${rule.themeId}.`);
        supportingFactorsHi.push(`पंच-सिद्धांत भाव विश्लेषण ${rule.themeId} के लिए जन्म कुंडली में अनुकूल स्थिति (${fiveRule.netPromiseScore}/100) दर्शाता है।`);
      } else if (fiveRule.netPromiseScore < 40) {
        limitingFactorsEn.push(`5-Rule PAE indicates underlying natal challenges (${fiveRule.netPromiseScore}/100) for ${rule.themeId}.`);
        limitingFactorsHi.push(`पंच-सिद्धांत भाव विश्लेषण ${rule.themeId} के लिए जन्म कालीन बाधाएं (${fiveRule.netPromiseScore}/100) दर्शाता है।`);
      }
    }
  } catch (_err) {
    // Gracefully handle if fiveRule calculation not applicable
  }

  const dashaScore = Math.max(15, Math.min(95, Math.round(rawDashaScore)));
  let dashaStatus: EvidenceStatus = 'NEUTRAL';
  if (dashaScore >= 75) dashaStatus = 'CONFIRMED';
  else if (dashaScore >= 60) dashaStatus = 'SUPPORTING';
  else if (dashaScore < 40) dashaStatus = 'CONFLICTING';

  const dashaEvidence: ParashariDashaEvidence = {
    mahadashaLord: mdLord,
    antardashaLord: adLord,
    pratyantardashaLord: undefined,
    lordDignityScore: Math.max(20, Math.min(90, 50 + (mdPl?.dignity === 'Exalted' ? 10 : 0))),
    activeHouses,
    status: dashaStatus,
    score: dashaScore,
    explanationEn: `Parashari Dasha (${mdLord}-${adLord}) activates houses [${activeHouses.join(', ')}], yielding score ${dashaScore}/100.`,
    explanationHi: `पराशरी दशा (${mdLord}-${adLord}) भाव [${activeHouses.join(', ')}] को सक्रिय करती है, स्कोर: ${dashaScore}/100।`
  };

  // 2. SAV & Transit evaluation
  const primaryHouse = rule.primaryHouses[0] || 1;
  const savBindus = getSavByBhava(kundali.ashtakvarga, primaryHouse, kundali.houses);

  let savAdjustment = 0;
  let savStatus: EvidenceStatus = 'NEUTRAL';
  if (savBindus >= 30) {
    savAdjustment = 10;
    savStatus = 'CONFIRMED';
    supportingFactorsEn.push(`SAV confirms high strength in primary house ${primaryHouse} with ${savBindus} bindus.`);
    supportingFactorsHi.push(`सर्वाष्टकवर्ग मुख्य भाव ${primaryHouse} में ${savBindus} बिंदुओं के साथ उच्च बल प्रदान करता है।`);
    traces.push({
      stream: 'PARASHARI',
      indicator: 'SAV Bindu Strength',
      finding: `House ${primaryHouse} holds ${savBindus} SAV bindus (Strong >28)`,
      findingHi: `भाव ${primaryHouse} में ${savBindus} सर्वाष्टकवर्ग बिंदु हैं (>28 उत्तम)`,
      impact: 'POSITIVE',
      weight: 0.15
    });
  } else if (savBindus < 25) {
    savAdjustment = -8;
    savStatus = 'CONFLICTING';
    limitingFactorsEn.push(`SAV bindus in primary house ${primaryHouse} are low (${savBindus}/28 baseline).`);
    limitingFactorsHi.push(`मुख्य भाव ${primaryHouse} में सर्वाष्टकवर्ग बिंदु कम हैं (${savBindus}/28 मानक)।`);
    traces.push({
      stream: 'PARASHARI',
      indicator: 'SAV Bindu Deficit',
      finding: `House ${primaryHouse} has low SAV bindus (${savBindus} < 25)`,
      findingHi: `भाव ${primaryHouse} में सर्वाष्टकवर्ग बिंदु कम हैं (${savBindus} < 25)`,
      impact: 'NEGATIVE',
      weight: 0.15
    });
  }

  const savEvidence: SAVModulationEvidence = {
    targetHouse: primaryHouse,
    savBindus,
    bavBindusOfTransitLord: Math.round(savBindus / 7),
    kakshyaLordActive: 'Jupiter',
    kakshyaHasBindu: savBindus >= 28,
    netAdjustmentPoints: savAdjustment,
    status: savStatus,
    score: Math.max(20, Math.min(95, Math.round(50 + (savBindus - 28) * 4))),
    explanationEn: `Primary House ${primaryHouse} holds ${savBindus} SAV bindus (Baseline: 28). Modulation: ${savAdjustment >= 0 ? '+' : ''}${savAdjustment}.`,
    explanationHi: `मुख्य भाव ${primaryHouse} में ${savBindus} सर्वाष्टकवर्ग बिंदु हैं (मानक: 28)। प्रभाव: ${savAdjustment >= 0 ? '+' : ''}${savAdjustment}।`
  };

  // Transit Evidence
  const jupiterHouse = ((2 - getSignIndex(lagnaSign) + 12) % 12) + 1; // approx
  const saturnHouse = ((11 - getSignIndex(lagnaSign) + 12) % 12) + 1;
  const rahuHouse = ((11 - getSignIndex(lagnaSign) + 12) % 12) + 1;
  const ketuHouse = ((5 - getSignIndex(lagnaSign) + 12) % 12) + 1;

  const transitScore = Math.max(20, Math.min(95, Math.round(50 + (rule.primaryHouses.includes(jupiterHouse) ? 20 : 0))));
  const transitStatus: EvidenceStatus = transitScore >= 70 ? 'CONFIRMED' : transitScore >= 55 ? 'SUPPORTING' : 'NEUTRAL';

  const transitEvidence: ParashariTransitEvidence = {
    jupiterTransitHouse: jupiterHouse,
    saturnTransitHouse: saturnHouse,
    rahuTransitHouse: rahuHouse,
    ketuTransitHouse: ketuHouse,
    aspectsEventHouses: rule.primaryHouses,
    aspectsDashaLord: false,
    status: transitStatus,
    score: transitScore,
    explanationEn: `Major planetary transits align with event houses [${rule.primaryHouses.join(', ')}].`,
    explanationHi: `प्रमुख ग्रहों का गोचर घटना भावों [${rule.primaryHouses.join(', ')}] से अनुकूल संबंध बना रहा है।`
  };

  const compositeParashariScore = Math.max(15, Math.min(95, Math.round((dashaScore * 0.55) + (savEvidence.score * 0.25) + (transitScore * 0.20))));
  let overallStatus: EvidenceStatus = 'NEUTRAL';
  if (compositeParashariScore >= 75) overallStatus = 'CONFIRMED';
  else if (compositeParashariScore >= 60) overallStatus = 'SUPPORTING';
  else if (compositeParashariScore < 40) overallStatus = 'CONFLICTING';

  return {
    dashaEvidence,
    transitEvidence,
    savEvidence,
    score: compositeParashariScore,
    status: overallStatus,
    supportingFactorsEn,
    supportingFactorsHi,
    limitingFactorsEn,
    limitingFactorsHi,
    traces
  };
}

/* ==========================================================================
   2. KRISHNAMURTI PADDHATI (KP) STREAM ADAPTER
   ========================================================================== */

export interface KPStreamOutput {
  readonly kpEvidence: KPEvidence;
  readonly score: V519Score;
  readonly status: EvidenceStatus;
  readonly supportingFactorsEn: string[];
  readonly supportingFactorsHi: string[];
  readonly limitingFactorsEn: string[];
  readonly limitingFactorsHi: string[];
  readonly traces: TriangulationTraceItem[];
}

export function evaluateKPStream(
  kundali: CompleteKundali,
  eventType: PredictiveEventType
): KPStreamOutput {
  const rule = getEventRuleDefinition(eventType);
  const primaryCuspNum = rule.primaryHouses[0] || 1;
  const supportingCusps = rule.supportingHouses;
  const cautionCusps = rule.cautionHouses;

  const supportingFactorsEn: string[] = [];
  const supportingFactorsHi: string[] = [];
  const limitingFactorsEn: string[] = [];
  const limitingFactorsHi: string[] = [];
  const traces: TriangulationTraceItem[] = [];

  let kpProfile: KPAnalysisResult | null = null;
  try {
    kpProfile = calculateKPProfile(kundali);
  } catch (_e) {
    kpProfile = null;
  }

  if (!kpProfile || !kpProfile.cusps || kpProfile.cusps.length === 0) {
    const fallbackEvidence: KPEvidence = {
      primaryCusp: primaryCuspNum,
      supportingCusps,
      subLordPlanet: 'Jupiter',
      starLordPlanet: 'Saturn',
      significatorLevels: { levelA: [], levelB: [], levelC: [], levelD: [] },
      signifiesEventHouses: false,
      status: 'UNAVAILABLE',
      score: 50,
      explanationEn: 'KP cusp calculation unavailable for current chart.',
      explanationHi: 'इस कुंडली के लिए केपी भाव स्पष्ट गणना उपलब्ध नहीं है।'
    };
    return {
      kpEvidence: fallbackEvidence,
      score: 50,
      status: 'UNAVAILABLE',
      supportingFactorsEn: [],
      supportingFactorsHi: [],
      limitingFactorsEn: [],
      limitingFactorsHi: [],
      traces: []
    };
  }

  const primaryCuspInfo = kpProfile.cusps.find(c => c.houseNumber === primaryCuspNum) || kpProfile.cusps[0];
  const subLord = (primaryCuspInfo?.subLord as PlanetName) || 'Jupiter';
  const starLord = (primaryCuspInfo?.starLord as PlanetName) || 'Saturn';

  // Gather 4-step significators for primary cusp
  const primarySig = kpProfile.significators.find(s => s.houseNumber === primaryCuspNum);
  const levelA = primarySig?.levelA_PlanetsInStarOfOccupants || [];
  const levelB = primarySig?.levelB_OccupantPlanets || [];
  const levelC = primarySig?.levelC_PlanetsInStarOfHouseLord || [];
  const levelD = primarySig?.levelD_HouseLord || [];

  // Check what houses the Sub-Lord signifies across the whole chart
  const subLordSignifiedHouses: number[] = [];
  for (const sig of kpProfile.significators) {
    const isLevelA = sig.levelA_PlanetsInStarOfOccupants.includes(subLord);
    const isLevelB = sig.levelB_OccupantPlanets.includes(subLord);
    const isLevelC = sig.levelC_PlanetsInStarOfHouseLord.includes(subLord);
    const isLevelD = sig.levelD_HouseLord.includes(subLord);
    if (isLevelA || isLevelB || isLevelC || isLevelD) {
      subLordSignifiedHouses.push(sig.houseNumber);
    }
  }

  const primaryFavorable = subLordSignifiedHouses.filter(h => rule.primaryHouses.includes(h));
  const supportingFavorable = subLordSignifiedHouses.filter(h => supportingCusps.includes(h));
  const cautionSignified = subLordSignifiedHouses.filter(h => cautionCusps.includes(h));

  let rawKPScore = 50;

  // Sub-Lord is king in KP:
  if (primaryFavorable.length > 0) {
    rawKPScore += primaryFavorable.length * 15;
    supportingFactorsEn.push(`KP Sub-Lord of Cusp ${primaryCuspNum} (${subLord}) directly signifies primary house(s) ${primaryFavorable.join(', ')}.`);
    supportingFactorsHi.push(`भाव ${primaryCuspNum} का केपी उप-स्वामी (${subLord}) मुख्य भाव ${primaryFavorable.join(', ')} का स्पष्ट कार्येश है।`);
    traces.push({
      stream: 'KP',
      indicator: `Cusp ${primaryCuspNum} Sub-Lord Signification`,
      finding: `Sub-Lord ${subLord} signifies favorable event house(s) ${primaryFavorable.join(', ')}`,
      findingHi: `उप-स्वामी ${subLord} अनुकूल घटना भाव ${primaryFavorable.join(', ')} को सूचित करता है`,
      impact: 'POSITIVE',
      weight: 0.40
    });
  }

  if (supportingFavorable.length > 0) {
    rawKPScore += supportingFavorable.length * 8;
    supportingFactorsEn.push(`KP Sub-Lord reinforces supporting cusp significations [${supportingFavorable.join(', ')}].`);
    supportingFactorsHi.push(`केपी उप-स्वामी सहायक भावों [${supportingFavorable.join(', ')}] को बल प्रदान करता है।`);
  }

  if (cautionSignified.length > 0) {
    rawKPScore -= cautionSignified.length * 14;
    limitingFactorsEn.push(`KP Sub-Lord simultaneously connects to caution/denial house(s) ${cautionSignified.join(', ')}.`);
    limitingFactorsHi.push(`केपी उप-स्वामी निषेध/बाधा भाव ${cautionSignified.join(', ')} से भी जुड़ा हुआ है।`);
    traces.push({
      stream: 'KP',
      indicator: 'KP Sub-Lord Caution House Link',
      finding: `Sub-Lord ${subLord} connects to caution house(s) ${cautionSignified.join(', ')}`,
      findingHi: `उप-स्वामी ${subLord} बाधा भाव ${cautionSignified.join(', ')} से संबंध बनाता है`,
      impact: 'NEGATIVE',
      weight: 0.35
    });
  }

  // Star-lord contribution
  if (rule.naturalKarakas.includes(starLord) || rule.primaryHouses.includes(primaryCuspNum)) {
    rawKPScore += 5;
  }

  const kpScore = Math.max(15, Math.min(95, Math.round(rawKPScore)));
  let kpStatus: EvidenceStatus = 'NEUTRAL';
  if (kpScore >= 75) kpStatus = 'CONFIRMED';
  else if (kpScore >= 60) kpStatus = 'SUPPORTING';
  else if (kpScore < 40) kpStatus = 'CONFLICTING';

  const kpEvidence: KPEvidence = {
    primaryCusp: primaryCuspNum,
    supportingCusps,
    subLordPlanet: subLord,
    starLordPlanet: starLord,
    significatorLevels: {
      levelA,
      levelB,
      levelC,
      levelD
    },
    signifiesEventHouses: primaryFavorable.length > 0 || supportingFavorable.length > 0,
    status: kpStatus,
    score: kpScore,
    explanationEn: `Cusp ${primaryCuspNum} Sub-Lord (${subLord}) signifies houses [${subLordSignifiedHouses.join(', ')}], resulting in KP Score ${kpScore}/100.`,
    explanationHi: `भाव ${primaryCuspNum} का उप-स्वामी (${subLord}) भाव [${subLordSignifiedHouses.join(', ')}] का कार्येश है, केपी स्कोर: ${kpScore}/100।`
  };

  return {
    kpEvidence,
    score: kpScore,
    status: kpStatus,
    supportingFactorsEn,
    supportingFactorsHi,
    limitingFactorsEn,
    limitingFactorsHi,
    traces
  };
}

/* ==========================================================================
   3. JAIMINI STREAM ADAPTER (CHARA DASHA, CHARA KARAKAS, RASHI DRISHTI, AL)
   ========================================================================== */

/**
 * Calculates classical Jaimini Rashi Drishti (Sign Aspects)
 * - Movable (Chara) signs aspect all Fixed signs EXCEPT the adjacent one.
 * - Fixed (Sthira) signs aspect all Movable signs EXCEPT the adjacent one.
 * - Dual (Dwisvabhava) signs aspect all other Dual signs.
 */
export function calculateRashiDrishti(sign: ZodiacSign): ZodiacSign[] {
  switch (sign) {
    case 'Aries': return ['Leo', 'Scorpio', 'Aquarius'];
    case 'Taurus': return ['Cancer', 'Libra', 'Capricorn'];
    case 'Gemini': return ['Virgo', 'Sagittarius', 'Pisces'];
    case 'Cancer': return ['Scorpio', 'Aquarius', 'Taurus'];
    case 'Leo': return ['Libra', 'Capricorn', 'Aries'];
    case 'Virgo': return ['Gemini', 'Sagittarius', 'Pisces'];
    case 'Libra': return ['Aquarius', 'Taurus', 'Leo'];
    case 'Scorpio': return ['Capricorn', 'Aries', 'Cancer'];
    case 'Sagittarius': return ['Gemini', 'Virgo', 'Pisces'];
    case 'Capricorn': return ['Taurus', 'Leo', 'Scorpio'];
    case 'Aquarius': return ['Aries', 'Cancer', 'Libra'];
    case 'Pisces': return ['Gemini', 'Virgo', 'Sagittarius'];
    default: return [];
  }
}

/**
 * Calculates Arudha Lagna (AL) House from Lagna
 */
export function calculateArudhaLagna(kundali: CompleteKundali): { houseNumber: number; sign: ZodiacSign } {
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const lagnaLord = ZODIAC_LORDS[lagnaSign] as PlanetName;
  const lordPlanet = kundali.planets.find(p => p.name === lagnaLord);
  const lordHouse = lordPlanet ? lordPlanet.house : 1;

  // Parashari & Jaimini rule: Count from Lagna (1) to Lord House (N).
  // Then count N houses from Lord House = 1 + 2*(lordHouse - 1).
  let alHouse = ((lordHouse - 1) * 2 + 1);
  while (alHouse > 12) alHouse -= 12;

  // Classical exception: If AL falls in 1st or 7th from Lagna, shift to 10th from it
  if (alHouse === 1) alHouse = 10;
  else if (alHouse === 7) alHouse = 4;

  const lagnaIdx = getSignIndex(lagnaSign);
  const alSign = ALL_SIGNS[(lagnaIdx + alHouse - 1) % 12];

  return { houseNumber: alHouse, sign: alSign };
}

/**
 * Calculates Upapada Lagna (UL) House from Lagna (Arudha of 12th House)
 */
export function calculateUpapadaLagna(kundali: CompleteKundali): { houseNumber: number; sign: ZodiacSign } {
  const h12 = kundali.houses.find(h => h.houseNumber === 12) || kundali.houses[11] || kundali.houses[0];
  const h12Sign = h12.sign;
  const h12Lord = ZODIAC_LORDS[h12Sign] as PlanetName;
  const lordPl = kundali.planets.find(p => p.name === h12Lord);
  const lordHouse = lordPl ? lordPl.house : 12;

  // Count from 12 to lordHouse
  const dist = ((lordHouse - 12 + 12) % 12);
  let ulHouse = ((lordHouse + dist - 1) % 12) + 1;

  if (ulHouse === 12) ulHouse = ((12 + 9 - 1) % 12) + 1; // 10th from 12th is 9th
  else if (ulHouse === 6) ulHouse = ((6 + 9 - 1) % 12) + 1; // 10th from 6th is 3rd

  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const lagnaIdx = getSignIndex(lagnaSign);
  const ulSign = ALL_SIGNS[(lagnaIdx + ulHouse - 1) % 12];

  return { houseNumber: ulHouse, sign: ulSign };
}

/**
 * Calculates 7 Chara Karakas if not already available in kundali
 */
export function getOrCalculateCharaKarakas(kundali: CompleteKundali): { karakaRole: 'AK' | 'AmK' | 'BK' | 'MK' | 'PK' | 'GK' | 'DK'; planet: PlanetName }[] {
  if (kundali.charaKarakas && kundali.charaKarakas.length >= 7) {
    return kundali.charaKarakas.map(ck => ({
      karakaRole: ck.code,
      planet: ck.planet
    }));
  }

  // Compute 7 Chara Karakas from 7 physical planets
  const eligiblePlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const planetDegs = kundali.planets
    .filter(p => eligiblePlanets.includes(p.name))
    .map(p => {
      const degInSign = p.longitude % 30;
      return { planet: p.name, degInSign };
    })
    .sort((a, b) => b.degInSign - a.degInSign);

  const roles: ('AK' | 'AmK' | 'BK' | 'MK' | 'PK' | 'GK' | 'DK')[] = ['AK', 'AmK', 'BK', 'MK', 'PK', 'GK', 'DK'];
  return planetDegs.slice(0, 7).map((p, idx) => ({
    karakaRole: roles[idx] || 'DK',
    planet: p.planet
  }));
}

/**
 * Calculates deterministic Jaimini Chara Dasha cycle and active sign for a given date
 */
export function calculateCharaDasha(
  kundali: CompleteKundali,
  targetDate: Date = new Date()
): { activeSign: ZodiacSign; sequence: { sign: ZodiacSign; years: number; startAge: number; endAge: number }[] } {
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const lagnaIdx = getSignIndex(lagnaSign);

  // Direct (Savya): Aries, Taurus, Gemini, Libra, Scorpio, Sagittarius
  // Indirect (Apasavya): Cancer, Leo, Virgo, Capricorn, Aquarius, Pisces
  const savyaSigns: ZodiacSign[] = ['Aries', 'Taurus', 'Gemini', 'Libra', 'Scorpio', 'Sagittarius'];
  const isDirect = savyaSigns.includes(lagnaSign);

  const sequence: { sign: ZodiacSign; years: number; startAge: number; endAge: number }[] = [];
  let currentAge = 0;

  for (let i = 0; i < 12; i++) {
    const signIdx = isDirect
      ? (lagnaIdx + i) % 12
      : ((lagnaIdx - i + 120) % 12);
    const sign = ALL_SIGNS[signIdx];
    const signLord = ZODIAC_LORDS[sign] as PlanetName;
    const lordPl = kundali.planets.find(p => p.name === signLord);
    const lordHouse = lordPl ? lordPl.house : 1;

    // Years = distance from sign to lord
    const signHouseFromLagna = ((signIdx - lagnaIdx + 12) % 12) + 1;
    let dist = ((lordHouse - signHouseFromLagna + 12) % 12);
    let years = dist === 0 ? 12 : dist;
    if (years === 0) years = 12;

    sequence.push({
      sign,
      years,
      startAge: currentAge,
      endAge: currentAge + years
    });
    currentAge += years;
  }

  const birthDate = new Date(kundali.birthDetails?.date || '1990-01-01');
  const ageYears = Math.max(0, (targetDate.getTime() - birthDate.getTime()) / (365.25 * 24 * 3600 * 1000));
  const cycleAge = ageYears % currentAge;

  const activePeriod = sequence.find(s => cycleAge >= s.startAge && cycleAge < s.endAge) || sequence[0];
  return {
    activeSign: activePeriod.sign,
    sequence
  };
}

export interface JaiminiStreamOutput {
  readonly jaiminiEvidence: JaiminiEvidence;
  readonly score: V519Score;
  readonly status: EvidenceStatus;
  readonly supportingFactorsEn: string[];
  readonly supportingFactorsHi: string[];
  readonly limitingFactorsEn: string[];
  readonly limitingFactorsHi: string[];
  readonly traces: TriangulationTraceItem[];
}

export function evaluateJaiminiStream(
  kundali: CompleteKundali,
  eventType: PredictiveEventType,
  targetDate: Date = new Date()
): JaiminiStreamOutput {
  const rule = getEventRuleDefinition(eventType);
  const supportingFactorsEn: string[] = [];
  const supportingFactorsHi: string[] = [];
  const limitingFactorsEn: string[] = [];
  const limitingFactorsHi: string[] = [];
  const traces: TriangulationTraceItem[] = [];

  const charaKarakas = getOrCalculateCharaKarakas(kundali);
  const al = calculateArudhaLagna(kundali);
  const ul = calculateUpapadaLagna(kundali);
  const charaDasha = calculateCharaDasha(kundali, targetDate);
  const activeSign = charaDasha.activeSign;

  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const activeSignHouse = getHouseFromSign(lagnaSign, activeSign);
  const rashiAspects = calculateRashiDrishti(activeSign);
  const rashiAspectHouses = rashiAspects.map(s => getHouseFromSign(lagnaSign, s));

  // Determine relevant Jaimini Karaka for event
  let primaryKarakaRole: 'AK' | 'AmK' | 'BK' | 'MK' | 'PK' | 'GK' | 'DK' = 'AmK';
  if (eventType === 'MARRIAGE') primaryKarakaRole = 'DK';
  else if (eventType === 'CHILDBIRTH_TIMING') primaryKarakaRole = 'PK';
  else if (eventType === 'PROPERTY_PURCHASE' || eventType === 'VEHICLE_ACQUISITION') primaryKarakaRole = 'MK';
  else if (eventType === 'HEALTH_SURGERY' || eventType === 'LITIGATION_RESOLUTION') primaryKarakaRole = 'GK';
  else if (eventType === 'SPIRITUAL_INITIATION') primaryKarakaRole = 'AK';
  else primaryKarakaRole = 'AmK';

  const primaryKaraka = charaKarakas.find(k => k.karakaRole === primaryKarakaRole) || charaKarakas[0];
  const karakaPlanet = kundali.planets.find(p => p.name === primaryKaraka.planet);
  const karakaHouse = karakaPlanet ? karakaPlanet.house : 10;

  let rawJaiminiScore = 50;

  // 1. Chara Dasha sign aspect on event houses
  const aspectsPrimary = rule.primaryHouses.some(h => h === activeSignHouse || rashiAspectHouses.includes(h));
  if (aspectsPrimary) {
    rawJaiminiScore += 18;
    supportingFactorsEn.push(`Active Chara Dasha sign (${activeSign}) aspects primary event house(s) [${rule.primaryHouses.join(', ')}] via Jaimini Rashi Drishti.`);
    supportingFactorsHi.push(`सक्रिय चर दशा राशि (${activeSign}) जैमिनी राशि दृष्टि द्वारा मुख्य घटना भाव [${rule.primaryHouses.join(', ')}] को देख रही है।`);
    traces.push({
      stream: 'JAIMINI',
      indicator: 'Chara Dasha Rashi Drishti',
      finding: `Chara Dasha sign ${activeSign} aspects event house(s) via Rashi Drishti`,
      findingHi: `चर दशा राशि ${activeSign} राशि दृष्टि द्वारा घटना भाव को देखती है`,
      impact: 'POSITIVE',
      weight: 0.35
    });
  }

  // 2. Event-specific Karaka alignment
  const isKarakaInActiveSignOrAspect = karakaPlanet && (
    karakaPlanet.sign === activeSign || rashiAspects.includes(karakaPlanet.sign as ZodiacSign)
  );

  if (isKarakaInActiveSignOrAspect) {
    rawJaiminiScore += 16;
    supportingFactorsEn.push(`Relevant Jaimini Karaka ${primaryKarakaRole} (${primaryKaraka.planet}) is energized by active Chara Dasha sign ${activeSign}.`);
    supportingFactorsHi.push(`प्रासंगिक जैमिनी कारक ${primaryKarakaRole} (${primaryKaraka.planet}) सक्रिय चर दशा राशि ${activeSign} द्वारा प्रभावी है।`);
    traces.push({
      stream: 'JAIMINI',
      indicator: `${primaryKarakaRole} Karaka Activation`,
      finding: `${primaryKarakaRole} (${primaryKaraka.planet}) activated by Chara Dasha sign`,
      findingHi: `${primaryKarakaRole} (${primaryKaraka.planet}) चर दशा राशि द्वारा सक्रिय है`,
      impact: 'POSITIVE',
      weight: 0.35
    });
  }

  // 3. Arudha Lagna (AL) / Upapada Lagna (UL) alignment
  if (eventType === 'MARRIAGE') {
    const ulAspect = ul.houseNumber === activeSignHouse || rashiAspectHouses.includes(ul.houseNumber);
    if (ulAspect) {
      rawJaiminiScore += 14;
      supportingFactorsEn.push(`Upapada Lagna (UL in ${ul.sign}) is directly aspected by current Chara Dasha sign.`);
      supportingFactorsHi.push(`उपपद लग्न (UL - ${ul.sign}) वर्तमान चर दशा राशि से दृष्ट है।`);
    }
  } else {
    const alAspect = al.houseNumber === activeSignHouse || rashiAspectHouses.includes(al.houseNumber);
    if (alAspect) {
      rawJaiminiScore += 10;
      supportingFactorsEn.push(`Arudha Lagna (AL in House ${al.houseNumber}) is aspected by Chara Dasha sign, conferring manifest external outcomes.`);
      supportingFactorsHi.push(`आरूढ़ लग्न (भाव ${al.houseNumber}) चर दशा राशि से दृष्ट है, जो प्रत्यक्ष फल प्रदाता है।`);
    }
  }

  // Caution houses check in Jaimini
  const cautionAspects = rule.cautionHouses.filter(h => h === activeSignHouse || rashiAspectHouses.includes(h));
  if (cautionAspects.length > 0 && !rule.primaryHouses.includes(activeSignHouse)) {
    rawJaiminiScore -= cautionAspects.length * 8;
    limitingFactorsEn.push(`Chara Dasha sign aspects Jaimini caution house(s) ${cautionAspects.join(', ')}.`);
    limitingFactorsHi.push(`चर दशा राशि जैमिनी प्रतिकूल भाव ${cautionAspects.join(', ')} को दृष्टि देती है।`);
  }

  const jaiminiScore = Math.max(15, Math.min(95, Math.round(rawJaiminiScore)));
  let jaiminiStatus: EvidenceStatus = 'NEUTRAL';
  if (jaiminiScore >= 75) jaiminiStatus = 'CONFIRMED';
  else if (jaiminiScore >= 60) jaiminiStatus = 'SUPPORTING';
  else if (jaiminiScore < 40) jaiminiStatus = 'CONFLICTING';

  const jaiminiEvidence: JaiminiEvidence = {
    activeCharaDashaSign: activeSign,
    activeKarakas: charaKarakas,
    rashiDrishtiHouses: rashiAspectHouses,
    arudhaLagnaHouse: al.houseNumber,
    upapadaLagnaHouse: ul.houseNumber,
    status: jaiminiStatus,
    score: jaiminiScore,
    explanationEn: `Active Chara Dasha sign (${activeSign}) and ${primaryKarakaRole} (${primaryKaraka.planet}) yield Jaimini score ${jaiminiScore}/100.`,
    explanationHi: `सक्रिय चर दशा राशि (${activeSign}) एवं ${primaryKarakaRole} (${primaryKaraka.planet}) से जैमिनी स्कोर: ${jaiminiScore}/100।`
  };

  return {
    jaiminiEvidence,
    score: jaiminiScore,
    status: jaiminiStatus,
    supportingFactorsEn,
    supportingFactorsHi,
    limitingFactorsEn,
    limitingFactorsHi,
    traces
  };
}

/* ==========================================================================
   4. THREE-STREAM SYNTHESIS & CONVERGENCE ENGINE
   ========================================================================== */

/**
 * Synthesizes Parashari, KP, and Jaimini streams into a deterministic TriangulationResult.
 */
export function triangulateEvent(
  kundali: CompleteKundali,
  eventType: PredictiveEventType,
  targetDate: Date = new Date(),
  weights: TriangulationWeights = DEFAULT_TRIANGULATION_WEIGHTS
): TriangulationResult {
  const rule = getEventRuleDefinition(eventType);

  // 1. Evaluate independent streams
  const parashariOutput = evaluateParashariStream(kundali, eventType);
  const kpOutput = evaluateKPStream(kundali, eventType);
  const jaiminiOutput = evaluateJaiminiStream(kundali, eventType, targetDate);

  const pScore = parashariOutput.score;
  const kScore = kpOutput.score;
  const jScore = jaiminiOutput.score;

  const pStatus = parashariOutput.status;
  const kStatus = kpOutput.status;
  const jStatus = jaiminiOutput.status;

  // 2. Methodological Weights & Availability Handling
  let totalActiveWeight = 0;
  let weightedScoreSum = 0;

  if (pStatus !== 'UNAVAILABLE') {
    totalActiveWeight += weights.PARASHARI;
    weightedScoreSum += pScore * weights.PARASHARI;
  }
  if (kStatus !== 'UNAVAILABLE') {
    totalActiveWeight += weights.KP;
    weightedScoreSum += kScore * weights.KP;
  }
  if (jStatus !== 'UNAVAILABLE') {
    totalActiveWeight += weights.JAIMINI;
    weightedScoreSum += jScore * weights.JAIMINI;
  }

  const baseWeightedScore = totalActiveWeight > 0
    ? weightedScoreSum / totalActiveWeight
    : 50;

  // 3. Convergence & Contradiction Multiplier
  const statuses = [pStatus, kStatus, jStatus];
  const confirmedCount = statuses.filter(s => s === 'CONFIRMED').length;
  const supportingCount = statuses.filter(s => s === 'SUPPORTING').length;
  const conflictingCount = statuses.filter(s => s === 'CONFLICTING').length;
  const unavailableCount = statuses.filter(s => s === 'UNAVAILABLE').length;

  let agreementMultiplier = 1.0;
  const contradictionsEn: string[] = [];
  const contradictionsHi: string[] = [];

  // Contradiction detection
  if (pStatus === 'CONFIRMED' && kStatus === 'CONFLICTING') {
    contradictionsEn.push(`Method Conflict: Parashari Dasha confirms activation (${pScore}/100), but KP Sub-Lord denies/delays (${kScore}/100).`);
    contradictionsHi.push(`पद्धतिगत विरोधाभास: पराशरी दशा अनुकूलता दर्शाती है (${pScore}/100), परंतु केपी उप-स्वामी विलंब/निषेध का संकेत देता है (${kScore}/100)।`);
  }
  if (pStatus === 'CONFIRMED' && jStatus === 'CONFLICTING') {
    contradictionsEn.push(`Method Conflict: Parashari Dasha is strong (${pScore}/100), while Jaimini Chara Dasha aspects caution houses (${jScore}/100).`);
    contradictionsHi.push(`पद्धतिगत विरोधाभास: पराशरी दशा प्रबल है (${pScore}/100), जबकि जैमिनी चर दशा प्रतिकूल भावों से जुड़ी है (${jScore}/100)।`);
  }
  if (kStatus === 'CONFIRMED' && pStatus === 'CONFLICTING') {
    contradictionsEn.push(`Method Conflict: KP Sub-Lord is strongly favorable (${kScore}/100), but Parashari Dasha lord is afflicted (${pScore}/100).`);
    contradictionsHi.push(`पद्धतिगत विरोधाभास: केपी उप-स्वामी अत्यंत अनुकूल है (${kScore}/100), परंतु पराशरी दशा स्वामी पीड़ित है (${pScore}/100)।`);
  }

  // Agreement factor calculation
  if (conflictingCount >= 2) {
    agreementMultiplier = 0.70; // Severe conflict
  } else if (conflictingCount === 1) {
    agreementMultiplier = 0.82; // Single conflict penalty
  } else if (confirmedCount + supportingCount === 3) {
    agreementMultiplier = 1.08; // Triple stream harmony bonus
  } else if (confirmedCount + supportingCount === 2 && unavailableCount === 0) {
    agreementMultiplier = 1.02; // Solid 2-stream support with neutral third
  } else if (unavailableCount >= 2) {
    agreementMultiplier = 0.88; // Limited data constraint
  }

  const rawConvergenceScore = Math.round(baseWeightedScore * agreementMultiplier);
  const convergenceScore = Math.max(15, Math.min(95, rawConvergenceScore));

  // 4. Final Assessment Classification
  let finalAssessment: FinalAssessmentStatus = 'MIXED';
  if (unavailableCount >= 2 && confirmedCount === 0 && supportingCount === 0) {
    finalAssessment = 'INSUFFICIENT_DATA';
  } else if (conflictingCount >= 2) {
    finalAssessment = 'CONTRADICTED';
  } else if (confirmedCount >= 2 && conflictingCount === 0 && convergenceScore >= 78) {
    finalAssessment = 'CONFIRMED';
  } else if ((confirmedCount + supportingCount >= 2) && conflictingCount === 0 && convergenceScore >= 68) {
    finalAssessment = 'STRONGLY_SUPPORTED';
  } else if ((confirmedCount + supportingCount >= 2) && convergenceScore >= 55) {
    finalAssessment = 'SUPPORTED';
  } else if (conflictingCount === 1) {
    finalAssessment = 'MIXED';
  } else if (confirmedCount + supportingCount === 1) {
    finalAssessment = 'WEAK';
  } else {
    finalAssessment = 'MIXED';
  }

  const confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW' =
    convergenceScore >= 72 && conflictingCount === 0 && unavailableCount <= 1
      ? 'HIGH'
      : convergenceScore >= 50
      ? 'MEDIUM'
      : 'LOW';

  // 5. Aggregate supporting & limiting factors
  const supportingFactorsEn = Array.from(new Set([
    ...parashariOutput.supportingFactorsEn,
    ...kpOutput.supportingFactorsEn,
    ...jaiminiOutput.supportingFactorsEn
  ]));

  const supportingFactorsHi = Array.from(new Set([
    ...parashariOutput.supportingFactorsHi,
    ...kpOutput.supportingFactorsHi,
    ...jaiminiOutput.supportingFactorsHi
  ]));

  const limitingFactorsEn = Array.from(new Set([
    ...parashariOutput.limitingFactorsEn,
    ...kpOutput.limitingFactorsEn,
    ...jaiminiOutput.limitingFactorsEn
  ]));

  const limitingFactorsHi = Array.from(new Set([
    ...parashariOutput.limitingFactorsHi,
    ...kpOutput.limitingFactorsHi,
    ...jaiminiOutput.limitingFactorsHi
  ]));

  const evidenceTrace: TriangulationTraceItem[] = [
    ...parashariOutput.traces,
    ...kpOutput.traces,
    ...jaiminiOutput.traces
  ];

  return {
    eventType,
    parashariEvidence: parashariOutput.dashaEvidence,
    transitEvidence: parashariOutput.transitEvidence,
    kpEvidence: kpOutput.kpEvidence,
    jaiminiEvidence: jaiminiOutput.jaiminiEvidence,
    savTransitModulation: parashariOutput.savEvidence,
    convergenceScore,
    confidenceLevel,
    finalAssessment,
    supportingFactorsEn,
    supportingFactorsHi,
    limitingFactorsEn,
    limitingFactorsHi,
    contradictionsEn,
    contradictionsHi,
    evidenceTrace,
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Enriches a PredictiveEventWindow with full Three-Stream Triangulation and Temporal Overlap
 */
export function triangulateEventWindow(
  kundali: CompleteKundali,
  eventWindow: PredictiveEventWindow,
  weights: TriangulationWeights = DEFAULT_TRIANGULATION_WEIGHTS
): TriangulatedEventWindow {
  const peakDateObj = new Date(eventWindow.peakDate);
  const validPeakDate = isNaN(peakDateObj.getTime()) ? new Date() : peakDateObj;

  const triangulationResult = triangulateEvent(kundali, eventWindow.eventType, validPeakDate, weights);

  // Evaluate temporal agreement between Dasha, KP, and Jaimini
  let temporalAgreement: TemporalAlignmentType = 'PARTIAL_OVERLAP';
  const pStatus = triangulationResult.parashariEvidence.status;
  const kStatus = triangulationResult.kpEvidence.status;
  const jStatus = triangulationResult.jaiminiEvidence.status;

  if (pStatus === 'CONFIRMED' && kStatus === 'CONFIRMED' && jStatus === 'CONFIRMED') {
    temporalAgreement = 'EXACT_OVERLAP';
  } else if ((pStatus === 'CONFIRMED' || pStatus === 'SUPPORTING') && (kStatus === 'CONFIRMED' || kStatus === 'SUPPORTING') && (jStatus === 'CONFIRMED' || jStatus === 'SUPPORTING')) {
    temporalAgreement = 'PARTIAL_OVERLAP';
  } else if (pStatus === 'CONFIRMED' || pStatus === 'SUPPORTING') {
    temporalAgreement = 'MACRO_OVERLAP';
  } else if (pStatus === 'CONFLICTING' && kStatus === 'CONFLICTING') {
    temporalAgreement = 'NO_OVERLAP';
  } else {
    temporalAgreement = 'PARTIAL_OVERLAP';
  }

  // Update eventWindow's triangulationEvidence with rich KP and Jaimini data
  const enrichedEvidenceGroup: TriangulationEvidenceGroup = {
    parashariDasha: triangulationResult.parashariEvidence,
    parashariTransit: triangulationResult.transitEvidence,
    kp: triangulationResult.kpEvidence,
    jaimini: triangulationResult.jaiminiEvidence,
    sav: triangulationResult.savTransitModulation
  };

  return {
    ...eventWindow,
    triangulationEvidence: enrichedEvidenceGroup,
    confidenceScore: triangulationResult.convergenceScore,
    triangulationResult,
    temporalAgreement
  };
}

/**
 * Triangulates all supported classical life events for a given Kundali.
 */
export function triangulateAllEvents(
  kundali: CompleteKundali,
  horizonMonths: 12 | 24 | 36 = 36
): TriangulatedEventWindow[] {
  const eventKeys = Object.keys(EVENT_RULE_REGISTRY) as PredictiveEventType[];
  return eventKeys.map(eventType => {
    const rawWindow = predictLifeEvent(kundali, eventType, horizonMonths);
    return triangulateEventWindow(kundali, rawWindow);
  });
}
